app.component("transcriptionProcessor", {
    templateUrl: "app/src/js/components/transcription/transcription-processor.html",
    controller: "TranscriptionProcessorController",
    bindings: {
        onInsertText: "&",
        practiceId: "<",
        patientId: "<",
        defaultPromptName: "@"
    }
});

app.controller("TranscriptionProcessorController", [
    "$scope",
    "TranscriptionAPI",
    "Session",
    function ($scope, TranscriptionAPI, Session) {
        const ctrl = this;

        ctrl.transcriptions = [];
        ctrl.prompts = [];
        ctrl.selectedPromptId = null;
        ctrl.selectedTranscriptionId = null;
        ctrl.userId = (Session && Session.user && Session.user.id != null)
            ? parseInt(Session.user.id, 10)
            : null;

        // Verify authorization for system level edits
        ctrl.canEditSystemPrompts = Session &&
            Session.user &&
            Session.user.features &&
            Session.user.features.editableSystemTranscriptionPromptsFeature;

        // Processing and visual state management
        ctrl.isProcessing = false;
        ctrl.showPromptManager = false;
        ctrl.editingPrompt = {};
        ctrl.favoriteCount = 0;
        ctrl.maxFavorites = 2;

        // AI Generation state management
        ctrl.aiGenerationInstruction = "";
        ctrl.isGeneratingPrompt = false;

        // Additional notes: free text the clinician adds to clarify or correct a recording. Drafts are
        // kept per transcription so switching recordings doesn't lose what was typed; they are saved
        // with the transcription (and processed with it) on Insert.
        ctrl.showNotes = false;
        ctrl.notesDrafts = {};
        ctrl.maxNotesLength = 5000;
        ctrl.processingError = null;

        ctrl.$onInit = function () {
            ctrl.cancelEdit(); // Reset the editing state first
            loadPrompts();     // Then safely load data and establish the default dropdown values
            loadTranscriptions();
        };

        $scope.$on('transcription-completed', function () {
            loadTranscriptions();
        });

        // Whether this user created the prompt. Compared as numbers, the way transcriptions are, so a
        // string id from the API still matches.
        ctrl.isMine = function (p) {
            return !!p && ctrl.userId !== null && parseInt(p.created_by_user_id, 10) === ctrl.userId;
        };

        // Recalculates the active favorite count based on current array state
        ctrl.updateFavoriteCount = function () {
            if (!ctrl.prompts) {
                ctrl.favoriteCount = 0;
                return;
            }
            ctrl.favoriteCount = ctrl.prompts.filter(p => ctrl.isMine(p) && p.is_favorite === true).length;
        };

        function loadPrompts(forceSelectedId = null) {
            TranscriptionAPI.getTranscriptionPrompts(ctrl.practiceId)
                .then(function (response) {
                    // Standardize all prompt IDs to strings right away
                    ctrl.prompts = (response.data || []).map(p => {
                        if (p.prompt_id != null) p.prompt_id = String(p.prompt_id);
                        return p;
                    });

                    ctrl.updateFavoriteCount();

                    // Explicitly force string matching for selections
                    if (forceSelectedId) {
                        ctrl.selectedPromptId = String(forceSelectedId);
                    } else if (!ctrl.selectedPromptId) {
                        // 1. Filter for favorites that belong specifically to the current user
                        const userFavorites = ctrl.prompts.filter(p =>
                            p.is_favorite === true && ctrl.isMine(p)
                        );

                        let favoritePrompt = null;

                        if (userFavorites.length > 0) {
                            // 2. If they have multiple favorites, try to use the default prompt name as a tiebreaker
                            if (ctrl.defaultPromptName && userFavorites.length > 1) {
                                favoritePrompt = userFavorites.find(p =>
                                    p.prompt_type &&
                                    p.prompt_type.toLowerCase() === ctrl.defaultPromptName.toLowerCase()
                                );
                            }

                            // 3. Fallback to the first user favorite if no tiebreaker matched
                            if (!favoritePrompt) {
                                favoritePrompt = userFavorites[0];
                            }
                        }

                        if (favoritePrompt) {
                            // Select the scoped favorite
                            ctrl.selectedPromptId = String(favoritePrompt.prompt_id);
                        } else if (ctrl.defaultPromptName && ctrl.prompts.length > 0) {
                            // Fallback to standard default if no user favorites exist
                            const match = ctrl.prompts.find(p =>
                                p.prompt_type &&
                                p.prompt_type.toLowerCase() === ctrl.defaultPromptName.toLowerCase()
                            );

                            if (match) {
                                ctrl.selectedPromptId = String(match.prompt_id);
                            }
                        }
                    }
                });
        }

        function loadTranscriptions() {
            TranscriptionAPI.getTranscriptionsByPatientId(ctrl.practiceId, ctrl.patientId)
                .then(function (response) {
                    let data = response.data || [];

                    // Transcriptions are private to the clinician who recorded them. The API only
                    // returns the user's own, so this is a second line of defence.
                    data = data.filter(t => parseInt(t.created_by_user_id, 10) === ctrl.userId);
                    data = data.filter(t => t.transcription_status === 'COMPLETED' || !t.transcription_status);
                    data.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));

                    ctrl.transcriptions = data.map(prepareTranscription);

                    if (ctrl.transcriptions.length > 0 && !ctrl.selectedTranscriptionId) {
                        ctrl.selectedTranscriptionId = String(ctrl.transcriptions[0].transcription_id);
                    }
                });
        }

        function prepareTranscription(t) {
            if (t.transcription_id != null) t.transcription_id = String(t.transcription_id); // Force string
            const text = t.raw_text || "";
            t.snippet = text.length > 35
                ? text.substring(0, 35).trim() + "..."
                : text;

            // Seed the draft from the saved notes, without overwriting anything typed but not yet saved.
            if (ctrl.notesDrafts[t.transcription_id] === undefined) {
                ctrl.notesDrafts[t.transcription_id] = t.additional_notes || "";
            }
            return t;
        }

        function getSelectedTranscription() {
            return ctrl.transcriptions.find(t => String(t.transcription_id) === String(ctrl.selectedTranscriptionId));
        }

        function getNotesDraft() {
            return (ctrl.notesDrafts[ctrl.selectedTranscriptionId] || "").trim();
        }

        // Whether the notes typed for the selected transcription differ from those saved with it.
        ctrl.hasUnsavedNotes = function () {
            const selectedTranscription = getSelectedTranscription();
            if (!selectedTranscription) return false;
            return getNotesDraft() !== (selectedTranscription.additional_notes || "").trim();
        };

        ctrl.hasNotes = function () {
            return getNotesDraft().length > 0;
        };

        ctrl.toggleNotes = function () {
            ctrl.showNotes = !ctrl.showNotes;
        };

        function replaceTranscription(updatedTranscription) {
            // Force the draft to the saved value, which is what was just sent.
            delete ctrl.notesDrafts[String(updatedTranscription.transcription_id)];
            prepareTranscription(updatedTranscription);

            const index = ctrl.transcriptions.findIndex(t => String(t.transcription_id) === String(updatedTranscription.transcription_id));
            if (index !== -1) {
                ctrl.transcriptions[index] = updatedTranscription;
            }
        }

        function getPreProcessedData(transcription, promptObj) {
            if (!transcription || !transcription.processed_text_list || !Array.isArray(transcription.processed_text_list) || !promptObj) {
                return null;
            }
            return transcription.processed_text_list.find(pt => {
                // 1. Strict ID Match: If both items have IDs, they MUST match perfectly.
                if (pt.prompt_id != null && promptObj.prompt_id != null) {
                    return String(pt.prompt_id) === String(promptObj.prompt_id);
                }

                // 2. Legacy Fallback: Only match by type if the pre-processed item is missing an ID.
                return pt.prompt_type != null && promptObj.prompt_type != null && pt.prompt_type === promptObj.prompt_type;
            }) || null;
        }

        ctrl.insert = function () {
            if (!ctrl.selectedTranscriptionId || !ctrl.selectedPromptId) return;

            const selectedTranscription = getSelectedTranscription();
            const selectedPrompt = ctrl.prompts.find(p => String(p.prompt_id) === String(ctrl.selectedPromptId));
            const additionalNotes = getNotesDraft();
            // Held for the request: the dropdown can be changed while it is in flight.
            const requestedTranscriptionId = ctrl.selectedTranscriptionId;

            if (!selectedPrompt) return;

            if (additionalNotes.length > ctrl.maxNotesLength) return;

            ctrl.processingError = null;

            const processedData = getPreProcessedData(selectedTranscription, selectedPrompt);

            let needsReprocessing = false;
            let localText = null;

            // Changed notes invalidate every result generated so far (the API discards them too).
            if (ctrl.hasUnsavedNotes() || !processedData) {
                needsReprocessing = true;
            } else {
                const currentPromptVersion = (selectedPrompt && selectedPrompt.prompt_version) ? String(selectedPrompt.prompt_version) : '0';
                const processedDataVersion = processedData.prompt_version ? String(processedData.prompt_version) : '0';

                if (currentPromptVersion !== processedDataVersion) {
                    needsReprocessing = true;
                } else {
                    localText = processedData.processed_text;
                }
            }

            if (!needsReprocessing && localText && ctrl.onInsertText) {
                ctrl.onInsertText({ text: localText.trim() });
            } else {
                ctrl.isProcessing = true;

                let requestPayload = {
                    patient_id: String(ctrl.patientId),
                    transcription_id: String(requestedTranscriptionId),
                    prompt_id: String(ctrl.selectedPromptId),
                    prompt_practice_id: selectedPrompt.practice_id,
                    prompt_type: selectedPrompt ? selectedPrompt.prompt_type : null,
                    prompt: selectedPrompt ? selectedPrompt.prompt : null,
                    additional_notes: additionalNotes
                };

                TranscriptionAPI.reprocessTranscription(ctrl.practiceId, requestPayload)
                    .then(function (response) {
                        // The response is the saved transcription; fall back to fetching it if not.
                        if (response.data && response.data.transcription_id) {
                            return response;
                        }
                        return TranscriptionAPI.getTranscription(ctrl.practiceId, requestedTranscriptionId);
                    })
                    .then(function (response) {
                        const updatedTranscription = response.data;

                        // Keep the local copy in step with what was saved (notes and results), so the
                        // next Insert does not reuse results generated before the notes changed.
                        replaceTranscription(updatedTranscription);

                        const newProcessedData = getPreProcessedData(updatedTranscription, selectedPrompt);
                        const text = (newProcessedData && newProcessedData.processed_text) || updatedTranscription.processed_text;

                        if (text && ctrl.onInsertText) {
                            ctrl.onInsertText({ text: text.trim() });
                        } else {
                            ctrl.processingError = "The transcription was processed, but no text was returned. Please try again.";
                        }
                    })
                    .catch(function (error) {
                        ctrl.processingError = "Failed to process the transcription. Please try again.";
                        console.error("Error reprocessing transcription", error && error.status);
                    })
                    .finally(function () {
                        ctrl.isProcessing = false;
                    });
            }
        };

        ctrl.togglePromptManager = function () {
            ctrl.showPromptManager = !ctrl.showPromptManager;
            if (!ctrl.showPromptManager) {
                ctrl.cancelEdit();
            }
        };

        ctrl.toggleFavorite = function (promptItem) {
            if (promptItem.scope === 'SYSTEM') {
                return;
            }
            const newStatus = !promptItem.is_favorite;

            if (newStatus && ctrl.favoriteCount >= ctrl.maxFavorites) {
                return;
            }

            promptItem.is_favorite = newStatus;
            ctrl.updateFavoriteCount();

            let requestPayload = {
                prompt_name: promptItem.prompt_name,
                prompt: promptItem.prompt,
                prompt_type: promptItem.prompt_type,
                scope: promptItem.scope,
                is_favorite: newStatus,
                include_vision: promptItem.include_vision || false
            };

            TranscriptionAPI.updateTranscriptionPrompt(promptItem.practice_id || ctrl.practiceId, promptItem.prompt_id, requestPayload)
                .then(function (response) { })
                .catch(function (error) {
                    console.error("Error updating favorite status", error && error.status);
                    loadPrompts();
                });
        };

        ctrl.generatePrompt = function () {
            if (!ctrl.aiGenerationInstruction) return;

            ctrl.isGeneratingPrompt = true;
            ctrl.generationError = null;

            const generateInstruction = ctrl.aiGenerationInstruction;
            const currentPromptContext = ctrl.editingPrompt.prompt || "";

            TranscriptionAPI.generateTranscriptionPrompt(ctrl.practiceId, generateInstruction, currentPromptContext)
                .then(function (response) {
                    let generatedText = "";

                    if (typeof response.data === 'string') {
                        generatedText = response.data;
                    } else if (response.data && response.data.prompt_text) {
                        generatedText = response.data.prompt_text;
                    } else if (response.data && response.data.generated_text) {
                        generatedText = response.data.generated_text;
                    }

                    if (generatedText) {
                        ctrl.editingPrompt.prompt = generatedText;
                        ctrl.aiGenerationInstruction = "";
                    } else {
                        ctrl.generationError = "Received empty response from AI.";
                    }
                })
                .catch(function (error) {
                    console.error("Failed to generate or refine prompt via AI", error && error.status);
                    ctrl.generationError = "Failed to process prompt. Please try again.";
                })
                .finally(function () {
                    ctrl.isGeneratingPrompt = false;
                });
        };

        ctrl.editPrompt = function (promptItem) {
            ctrl.editingPrompt = {
                practice_id: promptItem.practice_id,
                prompt_id: promptItem.prompt_id,
                prompt_name: promptItem.prompt_name,
                prompt: promptItem.prompt,
                prompt_type: promptItem.prompt_type,
                scope: promptItem.scope,
                is_favorite: promptItem.is_favorite || false,
                include_vision: promptItem.include_vision || false
            };
            ctrl.aiGenerationInstruction = "";
            ctrl.generationError = null;
        };

        ctrl.copyPrompt = function (promptItem) {
            ctrl.editingPrompt = {
                practice_id: ctrl.practiceId,
                prompt_id: null,
                prompt_name: promptItem.prompt_name + ' (Copy)',
                prompt: promptItem.prompt,
                prompt_type: null,
                is_favorite: false,
                include_vision: promptItem.include_vision || false
            };
            ctrl.aiGenerationInstruction = "";
            ctrl.generationError = null;
        };

        ctrl.cancelEdit = function () {
            ctrl.editingPrompt = {
                practice_id: null,
                prompt_id: null,
                prompt_name: '',
                prompt: '',
                prompt_type: '',
                scope: '',
                is_favorite: false,
                include_vision: false
            };
            ctrl.aiGenerationInstruction = "";
            ctrl.generationError = null;
        };

        ctrl.savePrompt = function () {
            if (!ctrl.editingPrompt.prompt_name || !ctrl.editingPrompt.prompt) return;

            if (ctrl.editingPrompt.is_favorite) {
                let isAlreadyFavorite = ctrl.prompts.some(p => p.prompt_id === ctrl.editingPrompt.prompt_id && p.is_favorite);
                if (!isAlreadyFavorite && ctrl.favoriteCount >= ctrl.maxFavorites) {
                    ctrl.editingPrompt.is_favorite = false;
                }
            }

            let requestPayload = {
                prompt_name: ctrl.editingPrompt.prompt_name,
                prompt: ctrl.editingPrompt.prompt,
                prompt_type: ctrl.editingPrompt.prompt_type || ctrl.editingPrompt.prompt_name.toUpperCase().replace(/\s+/g, '_'),
                scope: ctrl.editingPrompt.scope || 'PRACTICE',
                is_favorite: ctrl.editingPrompt.is_favorite || false,
                include_vision: ctrl.editingPrompt.include_vision || false
            };

            let practiceIdToUse = ctrl.editingPrompt.practice_id || ctrl.practiceId;
            let apiCall;

            if (ctrl.editingPrompt.prompt_id) {
                apiCall = TranscriptionAPI.updateTranscriptionPrompt(practiceIdToUse, ctrl.editingPrompt.prompt_id, requestPayload);
            } else {
                apiCall = TranscriptionAPI.createTranscriptionPrompt(ctrl.practiceId, requestPayload);
            }

            apiCall.then(function (response) {
                let newPromptId = (response.data && response.data.prompt_id) ? response.data.prompt_id : ctrl.editingPrompt.prompt_id;
                loadPrompts(newPromptId);
                ctrl.cancelEdit();
            }).catch(function (error) {
                console.error("Error saving transcription prompt", error && error.status);
            });
        };

        ctrl.deletePrompt = function (prompt) {
            if (!prompt || !confirm("Are you sure you want to delete this prompt?")) return;

            TranscriptionAPI.deleteTranscriptionPrompt(prompt.practice_id || ctrl.practiceId, prompt.prompt_id)
                .then(function () {
                    if (ctrl.selectedPromptId === prompt.prompt_id) {
                        ctrl.selectedPromptId = null;
                    }
                    if (ctrl.editingPrompt.prompt_id === prompt.prompt_id) {
                        ctrl.cancelEdit();
                    }
                    loadPrompts();
                }).catch(function (error) {
                    console.error("Error deleting transcription prompt", error && error.status);
                });
        };
    }
]);